import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, message, Checkbox, Spin } from 'antd';
import { CopyOutlined, WhatsAppOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { whatsappBusinessNumbers } from '../../../../api/Api';
import { useAspSelector } from '../../../../test/jest-redux-hooks';
import Duplicate from '../../../../assets/customSVG/Duplicate';
import { gainwix_icons } from '../../../../api/ApiConfig';

const STYLE_CDN_URL = `${gainwix_icons}/whatsapp-widget/style.css`;
const SCRIPT_CDN_URL = `${gainwix_icons}/whatsapp-widget/script.js`;

const sizeOptions = [
  { label: 'Small (50px)', value: 'small' },
  { label: 'Medium (60px)', value: 'medium' },
  { label: 'Large (70px)', value: 'large' }
];

const positionOptions = [
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Bottom Right', value: 'bottom-right' },
  { label: 'Top Left', value: 'top-left' },
  { label: 'Top Right', value: 'top-right' }
];

const sizeMap = { small: '50px', medium: '60px', large: '70px' };

const WhatsAppWidgetConfigurator = () => {
  const currentBrand = useAspSelector((state) => state.app.currentBrand);
  const [whatsappChannels, setWhatsappChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const [config, setConfig] = useState({
    phoneNumber: undefined,
    defaultMessage: "Hello! I'm interested in your services. Can you help me?",
    position: 'bottom-right',
    widgetSize: 'medium',
    pulseAnimation: true,
    showOnMobile: false
  });
  const [embedCode, setEmbedCode] = useState('');
  const [copiedState, setCopiedState] = useState(false);

  useEffect(() => {
    const fetchNumbers = async () => {
      if (!currentBrand?.brand_id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await whatsappBusinessNumbers(currentBrand.brand_id);
        const numbers = response?.data?.wapp_business_num;
        setWhatsappChannels(Array.isArray(numbers) ? numbers : []);
      } catch {
        setError('Failed to load WhatsApp numbers');
        message.error('Failed to load WhatsApp numbers');
        setWhatsappChannels([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNumbers();
  }, [currentBrand?.brand_id]);

  useEffect(() => { generateEmbedCode(); }, [config]);

  function generateEmbedCode() {
    if (!config.phoneNumber) {
      setEmbedCode('<!-- Please select a WhatsApp number first -->');
      return;
    }

    const attributes = [
      `data-whatsapp-number="${config.phoneNumber}"`,
      `data-message="${config.defaultMessage.replace(/"/g, '&quot;')}"`,
      `data-position="${config.position}"`,
      `data-size="${config.widgetSize}"`,
      `data-pulse="${config.pulseAnimation}"`,
      `data-hide-mobile="${config.showOnMobile}"`
    ];

    const embedCodeGenerated = `<!-- WhatsApp Widget -->
<link rel="stylesheet" href="${STYLE_CDN_URL}">
<script src="${SCRIPT_CDN_URL}" ${attributes.join(' ')}></script>`;

    setEmbedCode(embedCodeGenerated);
  }

  const handleConfigChange = (_, allFields) => setConfig(allFields);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopiedState(true);
      setTimeout(() => {
        setCopiedState(false);
      }, 2000);
    } catch {
      message.error('Failed to copy to clipboard');
    }
  };

  const PreviewWidget = () => {
    if (!config.phoneNumber) return (
      <div className="flex flex-col items-center justify-center h-56 bg-gradient-to-br from-gray-100 to-gray-300 rounded-xl border-dashed border-2 border-gray-200">
        <span className="text-gray-400">Select a WhatsApp number for preview</span>
      </div>
    );

    return (
      <div className="relative bg-gradient-to-br from-gray-100 to-gray-300 rounded-xl h-56 overflow-hidden shadow">
        <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20 pointer-events-none z-0">
          <div className="bg-white rounded w-40 h-6 mb-2"></div>
          <div className="bg-white rounded w-28 h-3 mb-1"></div>
          <div className="bg-white rounded w-20 h-3"></div>
        </div>
        <div className={`absolute ${config.position === 'bottom-left' ? 'left-5 bottom-5' : ''}${config.position === 'bottom-right' ? 'right-5 bottom-5' : ''}${config.position === 'top-left' ? 'left-5 top-5' : ''}${config.position === 'top-right' ? 'right-5 top-5' : ''} z-10`}>
          <div
            className={[
              "whatsapp-widget-preview",
              config.pulseAnimation ? "pulse-animation" : ""
            ].join(" ")}
            style={{
              backgroundColor: "#25D366",
              width: sizeMap[config.widgetSize],
              height: sizeMap[config.widgetSize],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              boxShadow: "0 4px 16px rgba(37, 211, 102, 0.4)",
              cursor: "pointer",
              position: "relative",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            <WhatsAppOutlined style={{ color: "#fff", fontSize: "1.8em", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))" }} />
          </div>
        </div>
        <style jsx>{`
          .whatsapp-widget-preview:hover {
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 8px 25px rgba(37, 211, 102, 0.6);
          }
          .pulse-animation {
            animation: whatsapp-pulse 2s infinite;
          }
          @keyframes whatsapp-pulse {
            0% { box-shadow: 0 4px 16px rgba(37, 211, 102, 0.4); }
            50% { box-shadow: 0 4px 16px rgba(37, 211, 102, 0.4), 0 0 0 10px rgba(37, 211, 102, 0.1); }
            100% { box-shadow: 0 4px 16px rgba(37, 211, 102, 0.4), 0 0 0 20px rgba(37, 211, 102, 0); }
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-50 min-h-[calc(100vh-56px)] p-6">
      <div className="mb-10">
        <h1 className="text-2xl font-extrabold text-green-600 mb-2">WhatsApp Widget Generator</h1>
        <p className="text-base text-gray-500 mb-4">Create your WhatsApp widget and copy the embed code below.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Config Form */}
        <Card className=" border border-gray-200 " bodyStyle={{ padding: 24 }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={config}
            onValuesChange={handleConfigChange}
            requiredMark={false}
          >
            <h2 className="text-xl font-bold mb-4 text-green-600">Widget Setup</h2>
            <Form.Item
              name="phoneNumber"
              label={<span className="font-semibold text-gray-700">WhatsApp Number</span>}
              rules={[{ required: true, message: 'Please select a WhatsApp number' }]}
              className="mb-4"
            >
              <Select
                placeholder="Select a WhatsApp number"
                size="large"
                dropdownStyle={{ fontSize: '16px' }}
                dropdownRender={(menu) => (
                  <div style={{ fontSize: '13px' }}>
                    {menu}
                  </div>
                )}
                loading={loading}
                notFoundContent={loading ? <Spin size="small" /> : error || 'No WhatsApp numbers found'}
                options={(Array.isArray(whatsappChannels) ? whatsappChannels : [])
                  .filter(channel => channel?.status_name === 'CONNECTED')
                  .map(channel => ({
                    value: `${channel?.country_code || ''}${channel?.send_from_phone_number || ''}`,
                    label: (
                      <div style={{ fontSize: '13px', padding: '8px 4px' }}>
                        {`${channel?.send_from_phone_name || 'Unknown'} (+${channel?.country_code || ''} ${channel?.send_from_phone_number || ''})`}
                      </div>
                    )
                  }))}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="defaultMessage"
              label={<span className="font-semibold text-gray-700 mt-2">Default Message</span>}
              rules={[{ required: true, message: 'Please enter default message' }]}
              className="mb-4"
            >
              <Input.TextArea
                rows={2}
                placeholder="Hello! I'm interested in your services. Can you help me?"
                maxLength={200}
              />
            </Form.Item>
            <div className="grid gap-4 mt-2 mb-4">
              <Form.Item name="position" label={<span className="font-semibold text-gray-700">Position</span>}>
                <Select 
                  size="large"
                  dropdownStyle={{ fontSize: '16px' }}
                  dropdownRender={(menu) => (
                    <div style={{ fontSize: '13px' }}>
                      {menu}
                    </div>
                  )}
                  options={positionOptions.map(option => ({
                    ...option,
                    label: <div style={{ fontSize: '13px', padding: '8px 4px' }}>{option.label}</div>
                  }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item name="widgetSize" label={<span className="font-semibold text-gray-700">Widget Size</span>}>
                <Select 
                  size="large"
                  dropdownStyle={{ fontSize: '16px' }}
                  dropdownRender={(menu) => (
                    <div style={{ fontSize: '13px' }}>
                      {menu}
                    </div>
                  )}
                  options={sizeOptions.map(option => ({
                    ...option,
                    label: <div style={{ fontSize: '13px', padding: '8px 4px' }}>{option.label}</div>
                  }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div className="mb-2 mt-6">
              <h3 className="font-semibold mb-3">Widget Options</h3>
              <Form.Item name="pulseAnimation" valuePropName="checked" className="mb-2">
                <Checkbox>Pulse animation</Checkbox>
              </Form.Item>
              <Form.Item name="showOnMobile" valuePropName="checked" className="mb-2">
                <Checkbox>Hide on mobile devices</Checkbox>
              </Form.Item>
            </div>
          </Form>
        </Card>
        {/* Preview & Code */}
        <div className="flex flex-col gap-6">
          <Card className="border border-gray-200" title={<span className="text-green-600 font-bold text-lg">Preview</span>} bodyStyle={{ padding: 24 }}>
            <PreviewWidget />
          </Card>
          <Card className="border border-gray-200" title={<span className="font-bold text-green-600 text-lg">Embed Code</span>} bodyStyle={{ padding: 24 }}>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Add this code to your <code className="bg-gray-100 px-1.5 py-0.5 rounded">&lt;head&gt;</code> section:</h4>
                  <Button
                    size="small"
                    icon={<Duplicate color={!config.phoneNumber ? `rgba(0, 0, 0, 0.25)` : `#059669`} />}
                    onClick={copyToClipboard}
                    disabled={!config.phoneNumber}
                    className={`flex !border-none !outline-none !shadow-none !hover:shadow-none !ring-0 items-center gap-1 ${!config.phoneNumber ? '!text-gray-400' : '!text-green-600 hover:!text-green-700'} !font-medium`}
                  >
                    {copiedState ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs overflow-x-auto select-all">
                  <pre>{embedCode}</pre>
                </div>
              </div>
              {/* <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                <div className="flex items-start">
                  <InfoCircleOutlined className="text-blue-400 mt-0.5 mr-2" />
                  <div className="text-sm text-blue-700">
                    <strong>Installation:</strong> Just copy and paste this code into the <code>&lt;head&gt;</code> section of your website. The widget will automatically appear on all pages.
                  </div>
                </div>
              </div> */}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppWidgetConfigurator;